import React, { useCallback } from 'react';
import { Download, Table2 } from 'lucide-react';
import type { FieldMeta } from '@/services/PagesServices/CustomReportBuilderService';
import { useReportBuilderState } from '../hooks/useReportBuilderState';
import type { ExportFormat } from '../constants';

interface TableViewProps {
  previewData: Record<string, unknown>[];
  previewFields: FieldMeta[];
  totalRecords: number;
  isLoading: boolean;
  isExporting: boolean;
  onExport: (format: ExportFormat) => void;
}

const TableView: React.FC<TableViewProps> = ({
  previewData,
  previewFields,
  totalRecords,
  isLoading,
  isExporting,
  onExport,
}) => {
  const sortBy = useReportBuilderState(s => s.sortBy);
  const sortOrder = useReportBuilderState(s => s.sortOrder);
  const currentPage = useReportBuilderState(s => s.currentPage);
  const pageSize = useReportBuilderState(s => s.pageSize);
  const reportName = useReportBuilderState(s => s.reportName);
  const setSortBy = useReportBuilderState(s => s.setSortBy);
  const setSortOrder = useReportBuilderState(s => s.setSortOrder);
  const setCurrentPage = useReportBuilderState(s => s.setCurrentPage);
  const setPageSize = useReportBuilderState(s => s.setPageSize);
  const setReportName = useReportBuilderState(s => s.setReportName);

  const totalPages = Math.ceil(totalRecords / pageSize);

  const handleColumnSort = useCallback((fieldKey: string) => {
    if (sortBy === fieldKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(fieldKey);
      setSortOrder('asc');
    }
  }, [sortBy, sortOrder, setSortBy, setSortOrder]);

  if (isLoading) {
    return (
      <div className="crb-loading">
        <div className="crb-spinner" />
        <p>Building report...</p>
      </div>
    );
  }

  if (previewData.length === 0) {
    return (
      <div className="crb-empty-state">
        <div className="crb-empty-icon"><Table2 size={36} /></div>
        <h3>Build Your Custom Report</h3>
        <p>Select a data source, choose the fields you want to include, apply filters, and click "Run Table Preview" to see your report.</p>
      </div>
    );
  }

  return (
    <>
      {/* Export bar */}
      <div className="crb-export-actions" style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
        <input
          type="text"
          className="crb-filter-input"
          placeholder="Report name"
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
          style={{ width: '150px' }}
        />
        <button className="crb-export-btn pdf" onClick={() => onExport('pdf')} disabled={isExporting}>
          <Download size={14} /> PDF
        </button>
        <button className="crb-export-btn excel" onClick={() => onExport('excel')} disabled={isExporting}>
          <Download size={14} /> Excel
        </button>
        <button className="crb-export-btn csv" onClick={() => onExport('csv')} disabled={isExporting}>
          <Download size={14} /> CSV
        </button>
      </div>

      {/* Table */}
      <div className="crb-table-wrapper">
        <table className="crb-table">
          <thead>
            <tr>
              {previewFields.map(field => (
                <th
                  key={field.key}
                  className={sortBy === field.key ? 'sorted' : ''}
                  onClick={() => handleColumnSort(field.key)}
                >
                  {field.label}
                  {sortBy === field.key && (
                    <span className="sort-indicator">{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewData.map((row, idx) => (
              <tr key={idx}>
                {previewFields.map(field => (
                  <td key={field.key} title={String(row[field.key] ?? '-')}>
                    {row[field.key] != null ? String(row[field.key]) : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="crb-pagination">
          <div className="crb-pagination-info">
            <span>Showing</span>
            <select className="crb-pagination-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>of {totalRecords.toLocaleString()}</span>
          </div>
          <div className="crb-pagination-buttons">
            <button className="crb-page-btn" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
              &#8249;
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (
                <button
                  key={pageNum}
                  className={`crb-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button className="crb-page-btn" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
              &#8250;
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(TableView);
